import React, { useState, useEffect, useCallback } from 'react';
import { Lock, LogOut, Download, RefreshCw, Users, MessageSquare, Mail, Plus, Trash2, CalendarDays, AlertTriangle, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { ProjectsPanel } from '../components/ProjectsPanel';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'fundora_admin_token';

const formatError = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  return String(detail);
};

const REGION_LABELS = {
  indonesia: 'Bali, Indonesia',
  maldives: 'Maldives',
  vietnam: 'Vietnam',
  cyprus: 'Northern Cyprus',
  multiple: 'Multiple Regions',
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const statusStyles = {
  new: 'bg-[#CDA259]/20 text-[#8a6a2f] border-[#CDA259]/40',
  contacted: 'bg-blue-50 text-blue-700 border-blue-200',
  closed: 'bg-gray-100 text-gray-600 border-gray-300',
};

const toCsv = (rows, headers) => {
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [headers.map((h) => escape(h.label)).join(','), ...rows.map((r) => headers.map((h) => escape(r[h.key])).join(','))].join('\n');
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

const LoginForm = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onSuccess(data);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-[#102131] to-[#1a3449] flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white/95 backdrop-blur rounded-2xl p-8 shadow-2xl"
      >
        <div className="w-12 h-12 rounded-full bg-[#CDA259]/15 flex items-center justify-center mb-6">
          <Lock className="text-[#CDA259]" size={22} />
        </div>
        <h1 className="text-3xl font-bold text-[#102131] mb-2">Admin Access</h1>
        <p className="text-sm text-gray-500 mb-8">Sign in to view consultation requests and subscribers.</p>

        <div className="space-y-5">
          <div>
            <Label htmlFor="admin-email" className="text-[#102131]">Email</Label>
            <Input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              className="mt-2 border-gray-300 focus:border-[#CDA259]"
            />
          </div>
          <div>
            <Label htmlFor="admin-password" className="text-[#102131]">Password</Label>
            <Input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="mt-2 border-gray-300 focus:border-[#CDA259]"
            />
          </div>

          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-600">{error}</p>
          )}

          <Button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90 font-semibold py-6"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </div>
      </form>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="bg-white rounded-xl border border-gray-200 p-6 flex items-center gap-4">
    <div className="w-11 h-11 rounded-full bg-[#CDA259]/12 flex items-center justify-center">
      <Icon className="text-[#CDA259]" size={20} />
    </div>
    <div>
      <p className="text-3xl font-bold text-[#102131] leading-none">{value}</p>
      <p className="text-xs uppercase tracking-wide text-gray-500 mt-1">{label}</p>
    </div>
  </div>
);

const Admin = () => {
  const { toast } = useToast();
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState('consultations');
  const [consultations, setConsultations] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [slots, setSlots] = useState([]);
  const [projects, setProjects] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [mailStatus, setMailStatus] = useState(null);
  const [newSlot, setNewSlot] = useState({ region: 'indonesia', date: '', time: '', meet_url: '' });
  const [previewLang, setPreviewLang] = useState('lt');
  const [previewRegion, setPreviewRegion] = useState('indonesia');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Admin | Fundora Global';
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setConsultations([]);
    setSubscribers([]);
    setSlots([]);
  }, []);

  const loadData = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setAuthed(false);
      return;
    }
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [cons, subs, slotRes, regRes, mailRes, projRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/consultations`, { headers }),
        axios.get(`${BACKEND_URL}/api/newsletter/subscribers`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/consultation-slots`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/registrations`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/email-queue`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/projects`, { headers }),
      ]);
      setConsultations(cons.data);
      setSubscribers(subs.data);
      setSlots(slotRes.data);
      setRegistrations(regRes.data);
      setMailStatus(mailRes.data);
      setProjects(projRes.data);
      setAuthed(true);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        toast({ title: 'Could not load data', description: formatError(err.response?.data?.detail), variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  }, [logout, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const changeStatus = async (id, status) => {
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const { data } = await axios.patch(
        `${BACKEND_URL}/api/consultations/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConsultations((prev) => prev.map((c) => (c.id === id ? data : c)));
      toast({ title: 'Lead updated', description: `Marked as ${status}.` });
    } catch (err) {
      toast({ title: 'Update failed', description: formatError(err.response?.data?.detail), variant: 'destructive' });
    }
  };

  const addSlot = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/consultation-slots`, newSlot, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSlots((prev) => [...prev, data].sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`)));
      setNewSlot({ region: 'indonesia', date: '', time: '', meet_url: '' });
      toast({ title: 'Consultation date added', description: `${data.consultation_id} — ${data.date} ${data.time}` });
    } catch (err) {
      toast({ title: 'Could not add date', description: formatError(err.response?.data?.detail), variant: 'destructive' });
    }
  };

  const removeSlot = async (id) => {
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/consultation-slots/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSlots((prev) => prev.filter((s) => s.id !== id));
      toast({ title: 'Consultation date removed' });
    } catch (err) {
      toast({ title: 'Could not remove date', description: formatError(err.response?.data?.detail), variant: 'destructive' });
    }
  };

  const openEmailPreview = async (kind) => {
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/email-preview`, {
        params: { kind, language: previewLang, region: previewRegion },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([data], { type: 'text/html;charset=utf-8' });
      window.open(URL.createObjectURL(blob), '_blank', 'noopener');
    } catch (err) {
      toast({ title: 'Could not open preview', description: formatError(err.response?.data?.detail), variant: 'destructive' });
    }
  };

  if (authed === null) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-[#F7FDFF]">
        <RefreshCw className="animate-spin text-[#CDA259]" size={28} />
      </div>
    );
  }

  if (authed === false) {
    return <LoginForm onSuccess={() => loadData()} />;
  }

  const newCount = consultations.filter((c) => c.status === 'new').length;

  return (
    <div className="min-h-screen pt-24 pb-20 bg-[#F7FDFF]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#CDA259] mb-2">Fundora Global</p>
            <h1 className="text-4xl font-bold text-[#102131]">Leads Dashboard</h1>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              data-testid="admin-refresh-btn"
              onClick={loadData}
              disabled={loading}
              className="border-gray-300 text-[#102131]"
            >
              <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button data-testid="admin-logout-btn" onClick={logout} className="bg-[#102131] text-white hover:bg-[#1a3449]">
              <LogOut size={16} className="mr-2" /> Log out
            </Button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          <StatCard icon={MessageSquare} label="Consultations" value={consultations.length} testId="stat-consultations" />
          <StatCard icon={Users} label="New / uncontacted" value={newCount} testId="stat-new-leads" />
          <StatCard icon={CalendarDays} label="Registrations" value={registrations.length} testId="stat-registrations" />
          <StatCard icon={Mail} label="Subscribers" value={subscribers.length} testId="stat-subscribers" />
        </div>

        <div className="flex items-center gap-2 mb-6 border-b border-gray-200">
          {[
            { key: 'consultations', label: `Consultation requests (${consultations.length})` },
            { key: 'registrations', label: `Registrations (${registrations.length})` },
            { key: 'subscribers', label: `Newsletter (${subscribers.length})` },
            { key: 'dates', label: `Consultation dates (${slots.length})` },
            { key: 'projects', label: `Projects (${projects.length})` },
          ].map((item) => (
            <button
              key={item.key}
              data-testid={`admin-tab-${item.key}`}
              onClick={() => setTab(item.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${
                tab === item.key ? 'border-[#CDA259] text-[#102131]' : 'border-transparent text-gray-500 hover:text-[#102131]'
              }`}
            >
              {item.label}
            </button>
          ))}
          <div className="ml-auto pb-2">
            <Button
              variant="outline"
              data-testid="admin-export-csv-btn"
              disabled={tab === 'dates' || tab === 'projects'}
              onClick={() => {
                if (tab === 'consultations') {
                  downloadCsv(
                    'fundora-consultations.csv',
                    toCsv(consultations, [
                      { key: 'created_at', label: 'Date' },
                      { key: 'name', label: 'Name' },
                      { key: 'email', label: 'Email' },
                      { key: 'phone', label: 'Phone' },
                      { key: 'region', label: 'Region' },
                      { key: 'consultation_date', label: 'Consultation date' },
                      { key: 'consultation_time', label: 'Consultation time' },
                      { key: 'message', label: 'Message' },
                      { key: 'status', label: 'Status' },
                    ])
                  );
                } else if (tab === 'registrations') {
                  downloadCsv(
                    'fundora-registrations.csv',
                    toCsv(registrations, [
                      { key: 'created_at', label: 'Registered at' },
                      { key: 'consultation_id', label: 'Consultation ID' },
                      { key: 'region', label: 'Region' },
                      { key: 'date', label: 'Date' },
                      { key: 'time', label: 'Time' },
                      { key: 'name', label: 'Name' },
                      { key: 'email', label: 'Email' },
                      { key: 'phone', label: 'Phone' },
                      { key: 'country', label: 'Country' },
                      { key: 'language', label: 'Language' },
                      { key: 'status', label: 'Status' },
                    ])
                  );
                } else {
                  downloadCsv(
                    'fundora-subscribers.csv',
                    toCsv(subscribers, [
                      { key: 'subscribed_at', label: 'Date' },
                      { key: 'email', label: 'Email' },
                      { key: 'active', label: 'Active' },
                    ])
                  );
                }
              }}
              className="border-gray-300 text-[#102131]"
            >
              <Download size={16} className="mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {tab === 'consultations' ? (
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto" data-testid="consultations-table">
            {consultations.length === 0 ? (
              <p className="p-10 text-center text-gray-500" data-testid="consultations-empty">No consultation requests yet.</p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="bg-[#102131] text-[#F7FDFF]">
                  <tr>
                    {['Date', 'Name', 'Contact', 'Region', 'Consultation date', 'Message', 'Status'].map((h) => (
                      <th key={h} className="px-5 py-4 font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {consultations.map((c) => (
                    <tr key={c.id} data-testid={`consultation-row-${c.id}`} className="border-t border-gray-100 hover:bg-[#F7FDFF] transition-colors duration-150">
                      <td className="px-5 py-4 whitespace-nowrap text-gray-500">
                        {new Date(c.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-4 font-medium text-[#102131] whitespace-nowrap">{c.name}</td>
                      <td className="px-5 py-4 text-gray-600">
                        <a href={`mailto:${c.email}`} className="text-[#102131] underline decoration-[#CDA259] block">{c.email}</a>
                        {c.phone && <span className="text-xs text-gray-500">{c.phone}</span>}
                      </td>
                      <td className="px-5 py-4 capitalize text-gray-600">{REGION_LABELS[c.region] || c.region}</td>
                      <td className="px-5 py-4 whitespace-nowrap text-gray-600" data-testid={`consultation-date-${c.id}`}>
                        {c.consultation_date ? `${c.consultation_date} · ${c.consultation_time}` : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-5 py-4 text-gray-600 max-w-sm">{c.message}</td>
                      <td className="px-5 py-4">
                        <select
                          data-testid={`consultation-status-${c.id}`}
                          value={c.status}
                          onChange={(e) => changeStatus(c.id, e.target.value)}
                          className={`text-xs font-medium rounded-full border px-3 py-1.5 capitalize cursor-pointer ${statusStyles[c.status] || statusStyles.new}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : tab === 'registrations' ? (
          <div className="space-y-5" data-testid="registrations-panel">
            <div className="rounded-xl border border-gray-200 bg-white p-5 flex flex-wrap items-end gap-4" data-testid="email-preview-box">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">Preview an email</p>
                <div className="flex flex-wrap gap-3">
                  <select
                    data-testid="preview-region-select"
                    value={previewRegion}
                    onChange={(e) => setPreviewRegion(e.target.value)}
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="indonesia">Bali, Indonesia</option>
                    <option value="maldives">Maldives</option>
                    <option value="vietnam">Vietnam</option>
                    <option value="cyprus">Northern Cyprus</option>
                  </select>
                  <select
                    data-testid="preview-language-select"
                    value={previewLang}
                    onChange={(e) => setPreviewLang(e.target.value)}
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="lt">Lietuvių</option>
                    <option value="en">English</option>
                    <option value="ru">Русский</option>
                    <option value="pl">Polski</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                {[
                  { kind: 'confirmation', label: 'Confirmation' },
                  { kind: 'reminder_24h', label: '24h reminder' },
                  { kind: 'reminder_1h', label: '1h reminder' },
                ].map((item) => (
                  <Button
                    key={item.kind}
                    variant="outline"
                    data-testid={`preview-${item.kind}-btn`}
                    onClick={() => openEmailPreview(item.kind)}
                    className="border-gray-300 text-[#102131]"
                  >
                    <Mail size={15} className="mr-2 text-[#CDA259]" /> {item.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-gray-500 w-full">Opens the real email in a new tab. Nothing is sent, no sending quota used.</p>
            </div>
            {mailStatus && !mailStatus.configured && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 flex items-start gap-3" data-testid="email-not-configured-warning">
                <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-900">
                  <strong>Emails are queued but not sending yet.</strong> Add your Resend API key to the backend
                  environment (RESEND_API_KEY) and every queued confirmation and reminder will be delivered automatically.
                  Pending right now: {mailStatus.pending}.
                </div>
              </div>
            )}
            {mailStatus && mailStatus.configured && (
              <div className="flex flex-wrap gap-6 text-sm text-gray-600" data-testid="email-status-summary">
                <span>Emails sent: <strong className="text-[#102131]">{mailStatus.sent}</strong></span>
                <span>Scheduled: <strong className="text-[#102131]">{mailStatus.pending}</strong></span>
                <span>Failed: <strong className="text-[#102131]">{mailStatus.failed}</strong></span>
              </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto" data-testid="registrations-table">
              {registrations.length === 0 ? (
                <p className="p-10 text-center text-gray-500" data-testid="registrations-empty">No registrations yet.</p>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#102131] text-[#F7FDFF]">
                    <tr>
                      {['Registered', 'Consultation', 'Name', 'Contact', 'Country', 'Lang', 'Emails'].map((h) => (
                        <th key={h} className="px-5 py-4 font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map((r) => (
                      <tr key={r.id} data-testid={`registration-row-${r.id}`} className="border-t border-gray-100 hover:bg-[#F7FDFF] transition-colors duration-150">
                        <td className="px-5 py-4 text-gray-500 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className="block font-medium text-[#102131]">{REGION_LABELS[r.region] || r.region}</span>
                          <span className="block text-xs text-gray-500">{r.date} · {r.time}</span>
                          <span className="block text-[10px] font-mono text-[#CDA259]">{r.consultation_id}</span>
                        </td>
                        <td className="px-5 py-4 font-medium text-[#102131] whitespace-nowrap">{r.name}</td>
                        <td className="px-5 py-4 text-gray-600">
                          <a href={`mailto:${r.email}`} className="text-[#102131] underline decoration-[#CDA259] block">{r.email}</a>
                          <span className="text-xs text-gray-500">{r.phone}</span>
                        </td>
                        <td className="px-5 py-4 text-gray-600 whitespace-nowrap">{r.country}</td>
                        <td className="px-5 py-4 text-gray-600 uppercase">{r.language}</td>
                        <td className="px-5 py-4">
                          <div className="flex flex-col gap-1">
                            {(r.emails || []).map((mail) => (
                              <span
                                key={mail.kind}
                                className={`inline-flex w-fit text-[11px] font-medium rounded-full border px-2.5 py-0.5 ${
                                  mail.status === 'sent' ? statusStyles.contacted
                                    : mail.status === 'failed' ? 'bg-red-50 text-red-700 border-red-200'
                                    : statusStyles.new
                                }`}
                              >
                                {mail.kind.replace('reminder_', '')} · {mail.status}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : tab === 'projects' ? (
          <ProjectsPanel
            projects={projects}
            token={localStorage.getItem(TOKEN_KEY)}
            onChanged={loadData}
            toast={toast}
            formatError={formatError}
          />
        ) : tab === 'subscribers' ? (
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto" data-testid="subscribers-table">
            {subscribers.length === 0 ? (
              <p className="p-10 text-center text-gray-500" data-testid="subscribers-empty">No subscribers yet.</p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="bg-[#102131] text-[#F7FDFF]">
                  <tr>
                    {['Date', 'Email', 'Status'].map((h) => (
                      <th key={h} className="px-5 py-4 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((s) => (
                    <tr key={s.id} data-testid={`subscriber-row-${s.id}`} className="border-t border-gray-100 hover:bg-[#F7FDFF] transition-colors duration-150">
                      <td className="px-5 py-4 text-gray-500 whitespace-nowrap">{new Date(s.subscribed_at).toLocaleDateString()}</td>
                      <td className="px-5 py-4 font-medium text-[#102131]">{s.email}</td>
                      <td className="px-5 py-4">
                        <span className={`text-xs font-medium rounded-full border px-3 py-1 ${s.active ? statusStyles.contacted : statusStyles.closed}`}>
                          {s.active ? 'Active' : 'Unsubscribed'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6" data-testid="slots-panel">
            <form
              onSubmit={addSlot}
              data-testid="add-slot-form"
              className="bg-white rounded-xl border border-gray-200 p-6 h-fit space-y-5"
            >
              <div className="flex items-center gap-2">
                <CalendarDays size={18} className="text-[#CDA259]" />
                <h3 className="font-semibold text-[#102131]">Add consultation date</h3>
              </div>

              <div>
                <Label htmlFor="slot-region" className="text-[#102131]">Region</Label>
                <select
                  id="slot-region"
                  data-testid="slot-region-select"
                  value={newSlot.region}
                  onChange={(e) => setNewSlot({ ...newSlot, region: e.target.value })}
                  className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#CDA259] outline-none"
                >
                  <option value="indonesia">Bali, Indonesia</option>
                  <option value="maldives">Maldives</option>
                  <option value="vietnam">Vietnam</option>
                  <option value="cyprus">Northern Cyprus</option>
                </select>
              </div>

              <div>
                <Label htmlFor="slot-date" className="text-[#102131]">Date</Label>
                <Input
                  id="slot-date"
                  data-testid="slot-date-input"
                  type="date"
                  required
                  min={todayStr()}
                  value={newSlot.date}
                  onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })}
                  className="mt-2 border-gray-300 focus:border-[#CDA259]"
                />
              </div>

              <div>
                <Label htmlFor="slot-time" className="text-[#102131]">Time (region local)</Label>
                <Input
                  id="slot-time"
                  data-testid="slot-time-input"
                  type="time"
                  required
                  value={newSlot.time}
                  onChange={(e) => setNewSlot({ ...newSlot, time: e.target.value })}
                  className="mt-2 border-gray-300 focus:border-[#CDA259]"
                />
              </div>

              <div>
                <Label htmlFor="slot-meet" className="text-[#102131]">Google Meet URL</Label>
                <Input
                  id="slot-meet"
                  data-testid="slot-meet-input"
                  type="url"
                  required
                  placeholder="https://meet.google.com/abc-defg-hij"
                  value={newSlot.meet_url}
                  onChange={(e) => setNewSlot({ ...newSlot, meet_url: e.target.value })}
                  className="mt-2 border-gray-300 focus:border-[#CDA259]"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Sent only in the confirmation email. The Consultation ID is generated automatically.
                </p>
              </div>

              <Button
                type="submit"
                data-testid="add-slot-submit"
                className="w-full bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90 font-semibold"
              >
                <Plus size={16} className="mr-2" /> Add date
              </Button>
            </form>

            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-x-auto" data-testid="slots-table">
              {slots.length === 0 ? (
                <p className="p-10 text-center text-gray-500" data-testid="slots-empty">No consultation dates yet.</p>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#102131] text-[#F7FDFF]">
                    <tr>
                      {['Region', 'Consultation ID', 'Date', 'Time', 'Meet', 'Status', ''].map((h, i) => (
                        <th key={i} className="px-5 py-4 font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {slots.map((s) => {
                      const past = s.date < todayStr();
                      return (
                        <tr key={s.id} data-testid={`slot-row-${s.id}`} className="border-t border-gray-100 hover:bg-[#F7FDFF] transition-colors duration-150">
                          <td className="px-5 py-4 font-medium text-[#102131] whitespace-nowrap">{REGION_LABELS[s.region] || s.region}</td>
                          <td className="px-5 py-4 font-mono text-xs text-[#CDA259] whitespace-nowrap">{s.consultation_id}</td>
                          <td className="px-5 py-4 text-gray-600 whitespace-nowrap">{s.date}</td>
                          <td className="px-5 py-4 text-gray-600 whitespace-nowrap">{s.time}</td>
                          <td className="px-5 py-4">
                            {s.meet_url ? (
                              <a
                                href={s.meet_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`slot-meet-link-${s.id}`}
                                className="inline-flex items-center gap-1.5 text-xs text-[#102131] underline decoration-[#CDA259]"
                              >
                                <Video size={13} /> Link
                              </a>
                            ) : (
                              <span className="text-xs text-red-600">missing</span>
                            )}
                          </td>
                          <td className="px-5 py-4">
                            <span className={`text-xs font-medium rounded-full border px-3 py-1 ${past ? statusStyles.closed : statusStyles.contacted}`}>
                              {past ? 'Past (hidden on site)' : 'Upcoming'}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-right">
                            <button
                              data-testid={`delete-slot-${s.id}`}
                              onClick={() => removeSlot(s.id)}
                              className="text-gray-400 hover:text-red-600 transition-colors duration-150"
                              aria-label="Delete consultation date"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
